// Frontend/src/components/common/ResponsiveModal.tsx
//
// Sheet on a phone, dialog on a desktop — one component so the two never
// drift apart. A modal that is merely narrow on a phone leaves the user
// scrolling a small box inside a page that also scrolls.
//
// This lives here rather than beside any one feature because the payments
// page had SIX hand-rolled modal shells, each with its own z-index, its own
// overlay colour, its own idea of how to close, and none of them usable on a
// phone. One shell is the fix.

import React from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOverlay } from "@/lib/overlay";

export default function ResponsiveModal({
  open,
  onClose,
  title,
  subtitle,
  headerAccent,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** A hairline of colour along the top of the header — usually the status. */
  headerAccent?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "md" | "lg";
}) {
  const { t } = useLanguage();
  useOverlay(open, onClose);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`
          absolute inset-x-0 bottom-0 top-10 flex flex-col overflow-hidden
          rounded-t-3xl bg-white shadow-2xl
          sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88vh] sm:w-[calc(100%-3rem)]
          sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl
          ${size === "lg" ? "sm:max-w-3xl" : "sm:max-w-2xl"}
        `}
      >
        {/* Grab handle: signals a sheet on touch, invisible on desktop. */}
        <div className="flex justify-center pt-2 sm:hidden" aria-hidden>
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        <header
          className="flex items-start gap-3 border-b border-gray-100 px-4 py-3 sm:px-5 sm:py-4"
          style={headerAccent ? { boxShadow: `inset 0 3px 0 0 ${headerAccent}` } : undefined}
        >
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold leading-snug text-gray-900 sm:text-base">
              {title}
            </div>
            {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="-mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-500 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
          {children}
        </div>

        {footer && (
          <footer className="border-t border-gray-100 bg-gray-50/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
